import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Play, Pause, Scissors, Share2, Download, Loader2, Check, Sparkles, Music, Type, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/db/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

interface VideoTemplate {
  id: string;
  name: string;
  description: string;
  category: 'opening' | 'transition' | 'subtitle' | 'music';
  thumbnail_url: string;
  config: any;
  is_premium: boolean;
}

interface VideoClipEditorProps {
  videoUrl: string;
  tutorialId: string;
  tutorialName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const VideoClipEditor: React.FC<VideoClipEditorProps> = ({
  videoUrl,
  tutorialId,
  tutorialName,
  isOpen,
  onClose,
}) => {
  const { user, profile } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(15);
  const [clipTitle, setClipTitle] = useState('');
  const [clipDescription, setClipDescription] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [clipGenerated, setClipGenerated] = useState(false);
  const [templates, setTemplates] = useState<VideoTemplate[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchTemplates = async () => {
      const { data } = await supabase.from('video_templates').select('*').order('sort_order');
      setTemplates(data || []);
    };
    fetchTemplates();
  }, []);

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const dur = videoRef.current.duration;
      setDuration(dur);
      setEndTime(Math.min(15, dur));
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      
      // 自动停止在结束时间
      if (videoRef.current.currentTime >= endTime) {
        videoRef.current.pause();
        setIsPlaying(false);
        videoRef.current.currentTime = startTime;
      }
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.currentTime = startTime;
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleStartTimeChange = (value: number[]) => {
    const newStart = value[0];
    setStartTime(newStart);
    if (newStart >= endTime) {
      setEndTime(Math.min(newStart + 1, duration));
    }
    if (videoRef.current) {
      videoRef.current.currentTime = newStart;
    }
  };

  const handleEndTimeChange = (value: number[]) => {
    const newEnd = value[0];
    setEndTime(newEnd);
    if (newEnd <= startTime) {
      setStartTime(Math.max(newEnd - 1, 0));
    }
  };

  const handleGenerateClip = async () => {
    if (!user) {
      toast.error('请先登录');
      return;
    }

    if (endTime - startTime > 15) {
      toast.error('片段时长不能超过 15 秒');
      return;
    }

    if (!clipTitle.trim()) {
      toast.error('请输入片段标题');
      return;
    }

    setIsProcessing(true);

    try {
      // 模拟视频剪辑处理（实际应调用视频处理服务）
      await new Promise(resolve => setTimeout(resolve, 2000));

      // 生成模拟的剪辑 URL（实际应上传到 Supabase Storage）
      const clipUrl = `${videoUrl}#t=${startTime},${endTime}`;

      // 构建模板配置
      const templateConfig = {
        opening: selectedTemplates.opening ? templates.find(t => t.id === selectedTemplates.opening)?.config : null,
        transition: selectedTemplates.transition ? templates.find(t => t.id === selectedTemplates.transition)?.config : null,
        subtitle: selectedTemplates.subtitle ? templates.find(t => t.id === selectedTemplates.subtitle)?.config : null,
        music: selectedTemplates.music ? templates.find(t => t.id === selectedTemplates.music)?.config : null,
      };

      // 保存到数据库
      const { error } = await supabase.from('video_clips').insert({
        user_id: user.id,
        original_tutorial_id: tutorialId,
        clip_url: clipUrl,
        start_time: startTime,
        end_time: endTime,
        duration: endTime - startTime,
        title: clipTitle,
        description: clipDescription + (Object.keys(selectedTemplates).length > 0 ? `\n\n应用模板: ${Object.keys(selectedTemplates).join(', ')}` : ''),
        is_shared_to_community: false,
      });

      if (error) throw error;

      // 完成任务：分享视频片段
      await supabase.rpc('complete_task', { task_key_param: 'share_clip' });

      setClipGenerated(true);
      toast.success('视频片段生成成功！已获得任务积分奖励 🎉');
    } catch (err: any) {
      toast.error(err.message || '生成失败');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleShareToCommunity = async () => {
    if (!user) return;

    try {
      // 创建社区帖子
      const { data: post, error: postError } = await supabase
        .from('community_posts')
        .insert({
          user_id: user.id,
          title: clipTitle,
          content: clipDescription || `分享来自《${tutorialName}》的精彩片段`,
          category: 'video',
        })
        .select()
        .single();

      if (postError) throw postError;

      // 更新视频片段记录
      const { error: updateError } = await supabase
        .from('video_clips')
        .update({
          is_shared_to_community: true,
          community_post_id: post.id,
        })
        .eq('user_id', user.id)
        .eq('title', clipTitle);

      if (updateError) throw updateError;

      toast.success('已分享到社区！');
      onClose();
    } catch (err: any) {
      toast.error(err.message || '分享失败');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'opening': return <Zap className="h-4 w-4" />;
      case 'transition': return <Sparkles className="h-4 w-4" />;
      case 'subtitle': return <Type className="h-4 w-4" />;
      case 'music': return <Music className="h-4 w-4" />;
      default: return null;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'opening': return '开场动画';
      case 'transition': return '转场特效';
      case 'subtitle': return '字幕样式';
      case 'music': return '背景音乐';
      default: return category;
    }
  };

  const templatesByCategory = (category: string) => {
    return templates.filter(t => t.category === category);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden rounded-2xl">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Scissors className="h-5 w-5 text-primary" />
            视频片段剪辑
          </DialogTitle>
          <DialogDescription>
            选择精彩片段（最长 15 秒），生成短视频并分享到社区
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 space-y-6">
          {/* 视频播放器 */}
          <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full h-full object-contain"
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
            />
            
            {/* 播放控制 */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
              <Button
                size="icon"
                className="h-12 w-12 rounded-full bg-white/90 hover:bg-white text-black shadow-lg"
                onClick={togglePlay}
              >
                {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
              </Button>
            </div>

            {/* 时间显示 */}
            <div className="absolute top-4 right-4 bg-black/70 px-3 py-1 rounded-full text-white text-sm font-mono">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          {/* 时间轴选择 */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">开始时间</span>
              <Badge variant="outline">{formatTime(startTime)}</Badge>
            </div>
            <Slider
              value={[startTime]}
              onValueChange={handleStartTimeChange}
              max={duration}
              step={0.1}
              className="w-full"
            />

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">结束时间</span>
              <Badge variant="outline">{formatTime(endTime)}</Badge>
            </div>
            <Slider
              value={[endTime]}
              onValueChange={handleEndTimeChange}
              max={duration}
              step={0.1}
              className="w-full"
            />

            <div className="flex items-center justify-between px-4 py-3 bg-muted/30 rounded-xl">
              <span className="text-sm font-medium text-muted-foreground">片段时长</span>
              <span className={`text-lg font-bold ${endTime - startTime > 15 ? 'text-red-500' : 'text-foreground'}`}>
                {formatTime(endTime - startTime)}
              </span>
            </div>
          </div>

          {/* 片段信息 */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">片段标题 *</label>
              <Input
                placeholder="给这个精彩片段起个名字"
                value={clipTitle}
                onChange={(e) => setClipTitle(e.target.value)}
                maxLength={50}
                className="rounded-xl"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">片段描述（选填）</label>
              <Textarea
                placeholder="描述一下这个片段的亮点..."
                value={clipDescription}
                onChange={(e) => setClipDescription(e.target.value)}
                maxLength={200}
                rows={3}
                className="rounded-xl resize-none"
              />
            </div>
          </div>

          {/* 模板库 */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-bold">应用模板（可选）</h3>
            </div>
            
            <Tabs defaultValue="opening" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="opening" className="text-xs">
                  <Zap className="h-3 w-3 mr-1" /> 开场
                </TabsTrigger>
                <TabsTrigger value="transition" className="text-xs">
                  <Sparkles className="h-3 w-3 mr-1" /> 转场
                </TabsTrigger>
                <TabsTrigger value="subtitle" className="text-xs">
                  <Type className="h-3 w-3 mr-1" /> 字幕
                </TabsTrigger>
                <TabsTrigger value="music" className="text-xs">
                  <Music className="h-3 w-3 mr-1" /> 音乐
                </TabsTrigger>
              </TabsList>

              {['opening', 'transition', 'subtitle', 'music'].map((category) => (
                <TabsContent key={category} value={category} className="space-y-2 mt-4">
                  <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                    {templatesByCategory(category).map((template) => (
                      <div
                        key={template.id}
                        className={cn(
                          "p-3 border rounded-xl cursor-pointer transition-all hover:shadow-md",
                          selectedTemplates[category] === template.id
                            ? "border-primary bg-primary/5"
                            : "border-border/50 hover:border-primary/50"
                        )}
                        onClick={() => {
                          if (template.is_premium && !profile?.is_premium) {
                            toast.error('该模板需要会员权限');
                            return;
                          }
                          setSelectedTemplates(prev => ({
                            ...prev,
                            [category]: prev[category] === template.id ? '' : template.id
                          }));
                        }}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <p className="text-sm font-bold line-clamp-1">{template.name}</p>
                          {template.is_premium && (
                            <Badge variant="secondary" className="text-[10px] px-1 h-4">VIP</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">{template.description}</p>
                        {selectedTemplates[category] === template.id && (
                          <div className="mt-2 flex items-center gap-1 text-primary text-xs font-medium">
                            <Check className="h-3 w-3" /> 已选择
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>

        <DialogFooter className="p-6 pt-4 flex gap-2">
          {!clipGenerated ? (
            <>
              <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">
                取消
              </Button>
              <Button
                onClick={handleGenerateClip}
                disabled={isProcessing || endTime - startTime > 15 || !clipTitle.trim()}
                className="flex-1 rounded-xl font-bold"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    生成中...
                  </>
                ) : (
                  <>
                    <Scissors className="h-4 w-4 mr-2" />
                    生成片段
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">
                <Check className="h-4 w-4 mr-2" />
                完成
              </Button>
              <Button onClick={handleShareToCommunity} className="flex-1 rounded-xl font-bold bg-primary">
                <Share2 className="h-4 w-4 mr-2" />
                分享到社区
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
